import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { API } from '@/api'
import { TeamMemberVolumeStat } from '@/api/type-response'

import { PAGE_SIZE } from '../constants'

interface UseDataMemberListOptions {
  teamResID?: string
  selectedRoles?: number[]
  enabled?: boolean
}

interface UseDataMemberListReturn {
  members: TeamMemberVolumeStat[]
  loading: boolean
  page: number
  total: number
  error: Error | null
  fetchMembers: (newPage?: number) => Promise<void>
  setPage: (page: number) => void
}

// Stable default values to avoid re-creating arrays on every render
const EMPTY_NUMBER_ARRAY: number[] = []

/**
 * Hook to fetch paginated team member volume statistics
 * Supports server-side filtering by role using member_res_ids parameter
 */
export function useDataMemberList(options: UseDataMemberListOptions): UseDataMemberListReturn {
  const { teamResID, enabled = true } = options

  // Use stable defaults to avoid reference changes
  const selectedRoles = options.selectedRoles ?? EMPTY_NUMBER_ARRAY

  const [members, setMembers] = useState<TeamMemberVolumeStat[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<Error | null>(null)

  // Stabilize all array dependencies using JSON serialization
  const rolesKey = useMemo(() => JSON.stringify(selectedRoles), [selectedRoles])

  // Store latest values in ref to avoid stale closures
  const filtersRef = useRef({ selectedRoles })
  filtersRef.current = { selectedRoles }

  // Stable fetch function - only depends on primitives and stable keys
  const fetchMembers = useCallback(
    async (newPage = 1) => {
      if (!enabled || !teamResID) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        const currentRoles = filtersRef.current.selectedRoles
        let memberResIds: string[] | undefined = undefined

        // If roles are selected, fetch member list first and filter by role
        if (currentRoles.length > 0) {
          const memberListRes = await API.getTeamMemberList({
            res_id: teamResID,
            page: 1,
            per_page: 1000, // Get all members for filtering
          })

          if (memberListRes.members) {
            // Filter members by selected roles
            const filteredMembers = memberListRes.members.filter((member) =>
              currentRoles.includes(member.user.role),
            )
            memberResIds = filteredMembers.map((m) => m.resID)

            // If no members match the role filter, return empty result
            if (memberResIds.length === 0) {
              setMembers([])
              setTotal(0)
              setPage(newPage)
              setLoading(false)
              return
            }
          }
        }

        // Fetch volume stats with member_res_ids filter (server-side filtering)
        const res = await API.getTeamMemberVolumeStats({
          res_id: teamResID,
          page: newPage,
          per_page: PAGE_SIZE,
          member_res_ids: memberResIds,
          only_fs: true,
        })

        if (res.volumes) {
          setMembers(res.volumes)
          setPage(newPage)
          setTotal(res.pagination?.total || 0)
        }
      } catch (err) {
        console.error('Failed to fetch member volume stats:', err)
        setError(err as Error)
      } finally {
        setLoading(false)
      }
    },
    [teamResID, enabled],
  )

  // Fetch on filter changes - only triggers when serialized keys change
  useEffect(() => {
    if (enabled) {
      fetchMembers(1)
    }
  }, [enabled, fetchMembers, rolesKey])

  return {
    members,
    loading,
    page,
    total,
    error,
    fetchMembers,
    setPage,
  }
}
